import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { probeSkillAgentSourceUrl } from "../../api/Skills/skillAgentSearchesApi";

/** Detect hub genérico (/men/, home) o SPA conocidos — aviso UI. */
export function isGenericHubUrl(url) {
  try {
    const u = new URL(String(url || "").trim());
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (
      /^(asos\.com|zalando\.[a-z.]+|farfetch\.com|instagram\.com)$/.test(host)
    ) {
      return true;
    }
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return true;
    if (
      parts.length === 1 &&
      /^(men|women|homme|femme|hombre|mujer|shop|sale)$/i.test(parts[0])
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function normalizeCustomSources(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((s, i) => {
      const url = String(s?.url || "").trim();
      if (!url) return null;
      let host = "";
      try {
        host = new URL(url).hostname.replace(/^www\./, "");
      } catch {
        host = url.slice(0, 40);
      }
      return {
        id: String(s?.id || `custom-src-${i + 1}`),
        name: String(s?.name || host || `Web ${i + 1}`).slice(0, 120),
        url,
        enabled: s?.enabled !== false,
        probe: s?.probe && typeof s.probe === "object" ? s.probe : null,
      };
    })
    .filter(Boolean);
}

/** Legacy customUrls string/array → customSources. */
export function customSourcesFromLegacy(customUrls) {
  const urls = Array.isArray(customUrls)
    ? customUrls
    : String(customUrls || "")
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
  return normalizeCustomSources(
    urls.map((url, i) => {
      let host = "";
      try {
        host = new URL(url).hostname.replace(/^www\./, "");
      } catch {
        host = url.slice(0, 40);
      }
      return {
        id: `custom-url-${i + 1}`,
        name: host || `Web ${i + 1}`,
        url,
        enabled: true,
      };
    }),
  );
}

export function customSourcesToUrls(sources = []) {
  return normalizeCustomSources(sources)
    .filter((s) => s.enabled)
    .map((s) => s.url);
}

function probeColor(level) {
  if (level === "ok") return "success.main";
  if (level === "adapter") return "info.main";
  if (level === "warn") return "warning.main";
  return "error.main";
}

/**
 * Lista editable de webs propias (fuentes custom del SEARCH).
 */
export default function SkillAgentCustomSourcesFields({
  sources = [],
  onChange,
  tipologia = "",
}) {
  const { t } = useTranslation();
  const list = useMemo(() => normalizeCustomSources(sources), [sources]);
  const [draftUrl, setDraftUrl] = useState("");
  const [draftName, setDraftName] = useState("");
  const [probing, setProbing] = useState(false);
  const [draftProbe, setDraftProbe] = useState(null);

  const setList = (next) => onChange?.(normalizeCustomSources(next));

  const addSource = async () => {
    const url = draftUrl.trim();
    if (!url || probing) return;
    let host = "";
    try {
      host = new URL(url).hostname.replace(/^www\./, "");
    } catch {
      /* keep */
    }
    setProbing(true);
    setDraftProbe(null);
    let probe = draftProbe;
    try {
      probe = await probeSkillAgentSourceUrl(url, tipologia);
      setDraftProbe(probe);
    } catch {
      probe = {
        ok: false,
        level: "warn",
        status: "probe_unreachable",
        message: t("skillAgent.customSources.probeUnreachable"),
      };
    } finally {
      setProbing(false);
    }
    const id = `custom-src-${Date.now()}`;
    setList([
      ...list,
      {
        id,
        name: (draftName.trim() || host || "Web").slice(0, 120),
        url,
        enabled: true,
        probe: probe
          ? {
              level: probe.level,
              status: probe.status,
              message: probe.message,
              tip: probe.tip || null,
              adapterHint: probe.adapterHint || null,
              ok: Boolean(probe.ok),
            }
          : null,
      },
    ]);
    setDraftUrl("");
    setDraftName("");
    setDraftProbe(null);
  };

  const toggle = (id, on) => {
    setList(list.map((s) => (s.id === id ? { ...s, enabled: on } : s)));
  };

  const remove = (id) => {
    setList(list.filter((s) => s.id !== id));
  };

  const rename = (id, name) => {
    setList(list.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  const reprobe = async (id, url) => {
    try {
      const probe = await probeSkillAgentSourceUrl(url, tipologia);
      setList(
        list.map((s) =>
          s.id === id
            ? {
                ...s,
                probe: {
                  level: probe.level,
                  status: probe.status,
                  message: probe.message,
                  tip: probe.tip || null,
                  adapterHint: probe.adapterHint || null,
                  ok: Boolean(probe.ok),
                },
              }
            : s,
        ),
      );
    } catch {
      /* keep previous */
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={800}
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.75,
          letterSpacing: 0.04,
          textTransform: "uppercase",
        }}
      >
        {t("skillAgent.customSources.heading")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {t("skillAgent.customSources.hint")}
      </Typography>

      <Stack spacing={0.75} sx={{ mb: 1.25 }}>
        {list.map((s) => (
          <Stack
            key={s.id}
            direction="row"
            alignItems="flex-start"
            spacing={0.5}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={s.enabled !== false}
                  onChange={(e) => toggle(s.id, e.target.checked)}
                />
              }
              label=""
              sx={{ mr: 0, ml: 0 }}
            />
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                value={s.name}
                onChange={(e) => rename(s.id, e.target.value)}
                placeholder={t("skillAgent.customSources.namePlaceholder")}
                fullWidth
                sx={{
                  "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.5 },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: "break-all" }}
              >
                {s.url}
              </Typography>
              {isGenericHubUrl(s.url) && (
                <Typography variant="caption" color="warning.main">
                  {t("skillAgent.customSources.hubWarningRow")}
                </Typography>
              )}
              {s.probe?.message && (
                <Typography
                  variant="caption"
                  color={probeColor(s.probe.level)}
                  sx={{ display: "block" }}
                >
                  {s.probe.message}
                  {s.probe.tip ? ` — ${s.probe.tip}` : ""}
                </Typography>
              )}
              {!s.probe && (
                <Button
                  size="small"
                  onClick={() => reprobe(s.id, s.url)}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none",
                    px: 0,
                    minWidth: 0,
                    fontSize: "0.7rem",
                  }}
                >
                  {t("skillAgent.customSources.checkUrl")}
                </Button>
              )}
            </Stack>
            <IconButton
              size="small"
              aria-label={t("skillAgent.customSources.ariaRemoveSource")}
              onClick={() => remove(s.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {!list.length && (
          <Typography variant="caption" color="text.secondary">
            {t("skillAgent.customSources.empty")}
          </Typography>
        )}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems="flex-start"
      >
        <TextField
          size="small"
          label={t("skillAgent.customSources.nameLabel")}
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          sx={{ width: { xs: "100%", sm: 140 } }}
        />
        <TextField
          size="small"
          label={t("skillAgent.customSources.urlLabel")}
          placeholder="https://tienda.com/collections/jackets"
          value={draftUrl}
          onChange={(e) => {
            setDraftUrl(e.target.value);
            setDraftProbe(null);
          }}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addSource();
            }
          }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={probing ? <CircularProgress size={14} /> : <AddIcon />}
          onClick={addSource}
          disabled={!draftUrl.trim() || probing}
          sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {probing
            ? t("skillAgent.customSources.checking")
            : t("skillAgent.customSources.add")}
        </Button>
      </Stack>
      {draftUrl.trim() && isGenericHubUrl(draftUrl) && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ mt: 0.75, display: "block" }}
        >
          {t("skillAgent.customSources.hubWarningDraft")}
        </Typography>
      )}
      {draftProbe?.message && (
        <Typography
          variant="caption"
          color={probeColor(draftProbe.level)}
          sx={{ mt: 0.75, display: "block" }}
        >
          {draftProbe.message}
          {draftProbe.tip ? ` — ${draftProbe.tip}` : ""}
        </Typography>
      )}
    </Box>
  );
}
